import { envValue, type EnvRecord } from "./envPreflight";

/**
 * V6 SUPPRESS. The suppression detector's switch, and the closed sets its
 * writers and events share. One rule for both the API (which queues
 * `suppression.detect` after each customer message) and the worker (which
 * reads the message and records).
 *
 * DEFAULT OFF IN CODE. The V design recommended ON at launch ("it only
 * restrains sending, and a missed STOP costs more than a restrained send"),
 * a departure from the shadow-first habit. That is Kabir's call and sits on
 * the decision list; the default here stays off until he rules. On is exactly
 * "1"; unset, empty or "0" is off; anything else is refused rather than
 * guessed (CLAUDE.md section 0).
 */

export const ENABLE_SUPPRESSION_DETECT_ENV = "ACKRA_ENABLE_SUPPRESSION_DETECT";

/** Null when the value is a valid flag, or the problem sentence. Never quotes the value. */
export function validateEnableSuppressionDetect(raw: string): string | null {
  if (raw === "0" || raw === "1") return null;
  return 'must be exactly "1" (the suppression detector on) or "0" (off). Any other value is refused rather than read as on';
}

/** True when the flag, read as set, is exactly "1". */
export function suppressionDetectRequested(env: EnvRecord): boolean {
  return envValue(env, ENABLE_SUPPRESSION_DETECT_ENV) !== undefined && env[ENABLE_SUPPRESSION_DETECT_ENV] === "1";
}

export function planSuppressionDetect(env: EnvRecord): boolean {
  if (envValue(env, ENABLE_SUPPRESSION_DETECT_ENV) === undefined) return false;
  const complaint = validateEnableSuppressionDetect(env[ENABLE_SUPPRESSION_DETECT_ENV] ?? "");
  if (complaint !== null) throw new Error(`${ENABLE_SUPPRESSION_DETECT_ENV} ${complaint}.`);
  return env[ENABLE_SUPPRESSION_DETECT_ENV] === "1";
}

/** Why a contact is suppressed. Stored as the arbiter's `reason` string. */
export const SUPPRESSION_REASONS = ["opt_out", "bereavement", "sensitive_life_change", "owner_other"] as const;
export type SuppressionReason = (typeof SUPPRESSION_REASONS)[number];

/** Where a suppression came from: a closed-list rule on an inbound message, or a person. */
export const SUPPRESSION_SOURCES = ["inbound_rule", "owner"] as const;
export type SuppressionSource = (typeof SUPPRESSION_SOURCES)[number];

export function isSuppressionReason(v: unknown): v is SuppressionReason {
  return typeof v === "string" && (SUPPRESSION_REASONS as readonly string[]).includes(v);
}
